import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Trash2, UserPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ROLE_BADGE, ROLE_DESCR, ROLE_LABEL, ROLE_LOGO, LOGOS } from "@/lib/constants";
import { PmBot } from "@/components/PmBot";

const EMPTY = { email: "", password: "", name: "", role: "dev", assegnatario: "", org: "Majorbit" };
const RUOLI = ["admin", "pm", "dev", "tester", "fornitore", "cliente_finale"];

export default function UtentiPage() {
  const [users, setUsers] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then(({ data }) => setUsers(data));

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
    api.get("/organizzazioni").then(({ data }) => setOrgs(data)).catch(() => setOrgs([]));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("Utente creato");
      setForm(EMPTY);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Utente eliminato");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!users)
    return (
      <div className="h-64 grid place-items-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  return (
    <div className="space-y-6" data-testid="utenti-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Amministrazione</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">Profili</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-500 md:text-base">
          Stefano Critelli (fornitore) aggiorna solo i propri task. Majorbit ha tre profili: PM in sola lettura,
          Dev che opera e consolida fino a In Test, Tester che dà OK o KO. Infocamere vede milestone e avanzamenti
          e scarica tutta la documentazione.
        </p>
      </div>

      <form onSubmit={create}
            className="grid items-end gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
            data-testid="create-user-form">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nome</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 className="mt-1.5 h-10" data-testid="new-user-name-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</Label>
          <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 className="mt-1.5 h-10" data-testid="new-user-email-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</Label>
          <Input required minLength={6} value={form.password}
                 onChange={(e) => setForm({ ...form, password: e.target.value })}
                 className="mt-1.5 h-10" data-testid="new-user-password-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ruolo</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger className="mt-1.5 h-10" data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {RUOLI.map((r) => (<SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Organizzazione</Label>
          <Select value={form.org || "-"} onValueChange={(v) => setForm({ ...form, org: v === "-" ? "" : v })}>
            <SelectTrigger className="mt-1.5 h-10" data-testid="new-user-org-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="-">Nessuna</SelectItem>
              {orgs.map((o) => (<SelectItem key={o.id} value={o.nome}>{o.nome}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assegnatario</Label>
          <Input value={form.assegnatario} placeholder="es. Stefano Critelli"
                 onChange={(e) => setForm({ ...form, assegnatario: e.target.value })}
                 className="mt-1.5 h-10" data-testid="new-user-assignee-input" />
        </div>
        <div className="sm:col-span-2 lg:col-span-3 xl:col-span-6">
          <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="role-hint">
            {ROLE_DESCR[form.role]}
          </p>
          <Button type="submit" disabled={busy}
                  className="h-10 bg-slate-900 text-white transition-colors hover:bg-slate-800 hover:text-white"
                  data-testid="create-user-button">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
            Crea profilo
          </Button>
        </div>
      </form>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                {["", "Nome", "Email", "Ruolo", "Organizzazione", "Assegnatario", ""].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 font-mono">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 last:border-0" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 w-12">
                    <img src={ROLE_LOGO[u.role] || LOGOS.governance} alt="" className="h-8 w-8 rounded-md object-cover" />
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${ROLE_BADGE[u.role]}`}>
                      {ROLE_LABEL[u.role]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.org || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{u.assegnatario || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => remove(u.id)}
                            className="text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                            data-testid={`delete-user-${u.email}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PmBot pagina="dashboard" />

    </div>
  );
}
